<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import MentionItem from 'dashboard/components/widgets/mentions/MentionItem.vue';

const props = defineProps({
  members: { type: Object, required: true },
  canMentionAll: { type: Boolean, default: false },
  query: { type: String, default: '' },
});
const emit = defineEmits(['mention', 'close']);
const { t } = useI18n();
const root = ref(null);
const selectedIndex = ref(0);
const items = computed(() => {
  const users = (props.members.users || [])
    .filter(member => member.user?.id)
    .map(({ user }) => ({
      id: user.id,
      label: user.display_name || user.name,
      description: user.email,
      thumbnail: user.thumbnail,
      type: 'user',
    }));
  const teams = (props.members.teams || [])
    .filter(member => member.team?.id)
    .map(({ team }) => ({
      id: team.id,
      label: team.name,
      description: team.description,
      type: 'team',
    }));
  return [
    ...(props.canMentionAll ? [{ id: '', label: 'all', type: 'all' }] : []),
    ...users,
    ...teams,
  ]
    .filter(item => {
      const label =
        item.type === 'all'
          ? `${item.label} ${t('INTERNAL_CHAT.MENTION_ALL')}`
          : item.label;
      return label.toLowerCase().includes(props.query.toLowerCase());
    })
    .map((item, index) => ({ ...item, index }));
});
const sections = computed(() =>
  [
    { type: 'all', title: '' },
    { type: 'user', title: t('CONVERSATION.MENTION.AGENTS') },
    { type: 'team', title: t('CONVERSATION.MENTION.TEAMS') },
  ]
    .map(section => ({
      ...section,
      items: items.value.filter(item => item.type === section.type),
    }))
    .filter(section => section.items.length)
);
const select = () => {
  if (items.value[selectedIndex.value])
    emit('mention', items.value[selectedIndex.value]);
};
const adjustScroll = () =>
  nextTick(() =>
    root.value
      ?.querySelector('[data-selected="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  );
const { moveSelectionUp, moveSelectionDown } = useKeyboardNavigableList({
  items,
  selectedIndex,
  onSelect: select,
  adjustScroll,
  registerKeyboardEvents: false,
});
watch(items, () => {
  selectedIndex.value = 0;
});
const onKeydown = event => {
  if (event.isComposing || event.keyCode === 229) return false;
  if (event.key === 'Escape') emit('close');
  else if (!items.value.length) return false;
  else if (event.key === 'ArrowUp') moveSelectionUp();
  else if (event.key === 'ArrowDown') moveSelectionDown();
  else if (
    event.key === 'Enter' &&
    !event.shiftKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.altKey
  )
    select();
  else return false;
  event.preventDefault();
  return true;
};
defineExpose({ onKeydown });
</script>

<template>
  <div
    ref="root"
    class="absolute bottom-full start-0 z-20 max-h-[12.5rem] w-full overflow-auto rounded-xl border border-solid border-n-strong bg-n-solid-1 p-1 text-sm leading-[1.2] shadow-md"
    role="menu"
    :aria-label="t('INTERNAL_CHAT.MENTION_MENU')"
  >
    <template v-for="section in sections" :key="section.type">
      <div
        v-if="section.title"
        class="px-2 py-2 text-xs font-medium tracking-wide capitalize text-n-slate-11"
      >
        {{ section.title }}
      </div>
      <button
        v-for="item in section.items"
        :key="`${item.type}-${item.id}`"
        type="button"
        class="flex w-full items-center rounded-md px-2 py-1 text-start hover:bg-n-alpha-black2"
        :class="{
          'bg-n-alpha-black2': selectedIndex === item.index,
          '[&_h5]:normal-case': item.type === 'all',
        }"
        role="menuitem"
        :data-selected="selectedIndex === item.index"
        :data-mention-type="item.type"
        :data-mention-id="item.id"
        @mousedown.prevent
        @mouseover="selectedIndex = item.index"
        @click="
          selectedIndex = item.index;
          select();
        "
      >
        <MentionItem
          :name="
            item.type === 'all'
              ? t('INTERNAL_CHAT.MENTION_ALL_TOKEN')
              : item.label
          "
          :description="
            item.type === 'all'
              ? t('INTERNAL_CHAT.MENTION_ALL')
              : item.description
          "
          :thumbnail="item.thumbnail"
          :selected="selectedIndex === item.index"
          :icon-name="item.type === 'all' ? 'i-lucide-users' : undefined"
        />
      </button>
    </template>
    <p v-if="!items.length" class="mb-0 px-3 py-2 text-xs text-n-slate-10">
      {{ t('INTERNAL_CHAT.NO_MENTIONS') }}
    </p>
  </div>
</template>
